export interface SSEEvent {
  event: string;
  data: Record<string, unknown>;
}

/**
 * Parse a chunk of an SSE byte stream into complete events plus a leftover
 * remainder to prepend to the next chunk. Pass `flush = true` at EOF to parse
 * a final event that was not terminated by a blank line.
 */
export function parseSSEBuffer(
  buffer: string,
  flush = false,
): {
  parsed: SSEEvent[];
  remaining: string;
} {
  // A trailing CR may be the first half of a CRLF split across chunks.
  const heldCR = !flush && buffer.endsWith('\r') ? '\r' : '';
  const normalized = (heldCR ? buffer.slice(0, -1) : buffer).replace(
    /\r\n?/g,
    '\n',
  );

  const lastBoundary = flush
    ? normalized.length
    : normalized.lastIndexOf('\n\n');
  if (lastBoundary === -1) {
    return { parsed: [], remaining: buffer };
  }

  const complete = normalized.slice(0, lastBoundary);
  const remaining = normalized.slice(lastBoundary + 2) + heldCR;
  const events: SSEEvent[] = [];

  for (const block of complete.split('\n\n')) {
    const parsed = parseBlock(block);
    if (parsed) {
      events.push(parsed);
    }
  }

  return { parsed: events, remaining };
}

function parseBlock(block: string): SSEEvent | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) {
      continue;
    }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }
    if (field === 'event') {
      event = value;
    } else if (field === 'data') {
      dataLines.push(value);
    }
  }

  if (!dataLines.length) {
    return null;
  }

  try {
    const data: unknown = JSON.parse(dataLines.join('\n'));

    return data && typeof data === 'object'
      ? { event, data: data as Record<string, unknown> }
      : null;
  } catch {
    return null;
  }
}
